/**
 * Execution Plan Card
 *
 * Renders a list of nodes that will be executed with per-node spark costs,
 * plus "Let's go!" / "Cancel" buttons. Used for cost confirmation questions.
 */

import { memo, useState } from 'react'
import { cn } from '../../lib/utils'
import { nodeSchemas } from '../canvas-v2/nodeRegistry'
import { EXECUTION_ACTION_LABELS } from '../../generated/nodeDataTypes'
import type { RerunNodeContext } from '../../generated/executionApprovals'
import { createNadDebug } from '../../utils/debugLog'
import { useTranslation } from 'react-i18next'
import { useGenerationCostEstimatesVisible } from '../../hooks/useGenerationCostEstimatesVisible'

const log = createNadDebug('dw:cost-confirm:action-label')

interface NodeDetail {
  node_id: string
  type: string // backend snake_case type
  /** Execution-layer action (for example, "run_speech_to_text"). Absent for
   *  the default generate path. Drives the credit-facing action label. */
  action?: string
  label: string
  sparks: number
  billingMode?: 'paid' | 'free_trial'
  batch_child?: boolean // true for panel nodes auto-created by storyboard tool
  rerunContext?: RerunNodeContext
}

type TranslateFn = ReturnType<typeof useTranslation>['t']

/** Look up a key, returning '' (not the key itself) when it is absent. */
function localizedOrEmpty(t: TranslateFn, key: string): string {
  const value = t(key, { defaultValue: '' })
  return typeof value === 'string' ? value : ''
}

/**
 * Resolve the descriptor shown under a node's label in the cost popup.
 *
 * Precedence is unchanged from the original: a labeled non-default action wins
 * (it replaces the misleading node-type label), then the node-type label, then
 * the raw backend type. What changed is the *source* at each tier — the
 * localized `shared:usageCostModal.*` maps are consulted first, and the
 * English schema label / generated `EXECUTION_ACTION_LABELS` are the fallback.
 *
 * Both localized maps already existed and were fully translated for en/ko/vi
 * (the Usage Cost modal reads them); this card just never used them, which is
 * why a Korean user saw "Video Generator" inside an otherwise-Korean card.
 */
function resolveDescriptor(
  node: NodeDetail,
  t: TranslateFn,
): { subtitle: string; usedActionLabel: boolean; usedFallback: boolean } {
  if (node.action) {
    const localizedAction = localizedOrEmpty(t, `shared:usageCostModal.action.${node.type}.${node.action}`)
    if (localizedAction) {
      return { subtitle: localizedAction, usedActionLabel: true, usedFallback: false }
    }
    const generatedAction = EXECUTION_ACTION_LABELS[node.type]?.[node.action]
    if (generatedAction) {
      return { subtitle: generatedAction, usedActionLabel: true, usedFallback: true }
    }
  }

  const localizedType = localizedOrEmpty(t, `shared:usageCostModal.nodeType.${node.type}`)
  if (localizedType) {
    return { subtitle: localizedType, usedActionLabel: false, usedFallback: false }
  }

  return {
    subtitle: backendTypeInfo[node.type]?.label ?? node.type,
    usedActionLabel: false,
    usedFallback: true,
  }
}

export interface ExecutionPlanCardProps {
  nodes: NodeDetail[]
  totalSparks: number
  selectedOption?: string
  onSelect: (option: string) => void
  onNodeClick?: (nodeId: string) => void
  disabled?: boolean
  /** True while the confirm/cancel REST call is in flight. */
  loading?: boolean
  /** When true, the card omits its own "Execution Plan" header.  Used by
   *  ``ExecutionPlanStack`` so the title row can host shared chrome
   *  (tabs, fold/expand chevron) and the card body stays compact. */
  hideHeader?: boolean
}

// Build a reverse lookup: backend_type → { icon, label } from nodeSchemas
const backendTypeInfo: Record<string, { icon: string; label: string }> = {}
for (const [, schema] of Object.entries(nodeSchemas)) {
  if (schema.backendType) {
    backendTypeInfo[schema.backendType] = { icon: schema.icon ?? '', label: schema.label ?? schema.backendType }
  }
}

/**
 * Collapse batch_child nodes (e.g. storyboard panels with in_update=True)
 * into their parent row. Shows "+ N panels" on the parent instead.
 */
function collapseNodes(nodes: NodeDetail[]): { visible: NodeDetail[]; childCountByParent: Map<string, number> } {
  const batchChildren = nodes.filter((n) => n.batch_child)
  const visible = nodes.filter((n) => !n.batch_child)

  const childCountByParent = new Map<string, number>()
  if (batchChildren.length > 0) {
    // Match children to their parent by shared timestamp suffix
    for (const child of batchChildren) {
      const tsMatch = child.node_id.match(/_(\d+)$/)
      if (!tsMatch) continue
      const ts = tsMatch[1]
      const parent = visible.find((n) => n.node_id.endsWith(ts))
      if (parent) {
        childCountByParent.set(parent.node_id, (childCountByParent.get(parent.node_id) ?? 0) + 1)
      }
    }
  }
  return { visible, childCountByParent }
}

export const ExecutionPlanCard = memo(function ExecutionPlanCard({
  nodes,
  totalSparks,
  selectedOption,
  onSelect,
  onNodeClick,
  disabled = false,
  loading = false,
  hideHeader = false,
}: ExecutionPlanCardProps) {
  const hasSelection = !!selectedOption
  const { t } = useTranslation('chat')
  const showEstimates = useGenerationCostEstimatesVisible()

  const handleSelect = (value: string) => {
    if (disabled || loading || hasSelection) return
    onSelect(value)
  }

  const { visible, childCountByParent } = collapseNodes(nodes)

  const statusSuffix = hasSelection
    ? selectedOption === 'confirm'
      ? ` ${t('executionPlan.approved')}`
      : ` ${t('executionPlan.cancelled')}`
    : ''

  const [nodesExpanded, setNodesExpanded] = useState(false)
  const collapsedShownCount = 1
  const hiddenNodeCount = Math.max(0, visible.length - collapsedShownCount)
  const nodesToRender = nodesExpanded ? visible : visible.slice(0, collapsedShownCount)
  const creditLabel = (count: number, billingMode?: 'paid' | 'free_trial') =>
    billingMode === 'free_trial' && count > 0
      ? t('executionPlan.free')
      : t('executionPlan.credit', { count })
  const totalIsFreeTrial = totalSparks === 0 && nodes.some((node) =>
    node.billingMode === 'free_trial' && node.sparks > 0)

  return (
    <div className="my-4 animate-fade-in-down">
      {/* Node list */}
      <div className="rounded-lg border border-border-subtle overflow-hidden">
        {!hideHeader && (
          <div className="px-3 py-2 bg-surface-1 border-b border-border-subtle">
            <span className="text-xs font-medium text-text-muted uppercase tracking-wider">
              {t('executionPlan.title')}{statusSuffix}
            </span>
          </div>
        )}
        <div className="divide-y divide-border-subtle">
          {nodesToRender.map((node) => {
            const info = backendTypeInfo[node.type]
            const icon = info?.icon ?? ''
            const { subtitle, usedActionLabel, usedFallback } = resolveDescriptor(node, t)
            log({
              // Event name is the RFC-0805 trace contract, consumed by
              // scripts/trace-cost-confirm-action-label.sh — do not rename.
              event: 'cost-confirm:action-label',
              node_id: node.node_id,
              type: node.type,
              action: node.action ?? null,
              subtitle,
              usedActionLabel,
              // True when no localized entry existed and we fell back to the
              // English schema/generated label — the signal to add a key.
              usedFallback,
            })
            const childCount = childCountByParent.get(node.node_id)
            return (
              <div
                key={node.node_id}
                className={cn(
                  'px-3 py-2 flex items-center gap-2',
                  onNodeClick && 'cursor-pointer hover:bg-surface-2 transition-colors',
                )}
                onClick={() => onNodeClick?.(node.node_id)}
              >
                {icon && <span className="text-sm flex-shrink-0">{icon}</span>}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-text-primary truncate">
                    {node.label}
                    {childCount != null && (
                      <span className="text-text-muted font-normal"> {t('executionPlan.panels', { count: childCount })}</span>
                    )}
                  </div>
                  <div className="text-xs font-medium text-text-muted">{subtitle}</div>
                  {node.rerunContext?.display_action === 'rerun' && (
                    <div className="mt-2 rounded-lg border border-border-subtle bg-surface-0 p-2 text-xs text-muted-foreground space-y-1.5" data-testid={`rerun-context-${node.node_id}`}>
                      <div className="font-medium text-foreground">
                        {t('executionPlan.rerun')}
                        <span className="ml-2 font-normal text-muted-foreground">
                          {node.rerunContext.earlier_runs_untracked
                            ? t('executionPlan.earlierRunsUntracked')
                            : t('executionPlan.trackedAttempt', { count: node.rerunContext.tracked_attempt })}
                        </span>
                      </div>
                      {node.rerunContext.task_owner === 'Director' && (
                        <div className="break-words whitespace-pre-wrap">
                          <span className="font-medium">{t('executionPlan.directorNote')} </span>
                          {node.rerunContext.director_reason || t('executionPlan.reasonMissing')}
                        </div>
                      )}
                      <div>
                        <span className="font-medium">{t('executionPlan.changed')} </span>
                        {!node.rerunContext.comparison_available
                          ? t('executionPlan.comparisonUnavailable')
                          : node.rerunContext.changed_fields.length
                          ? node.rerunContext.changed_fields.map((field) =>
                            t(`executionPlan.changedField.${field}`, { defaultValue: field })).join(', ')
                          : t('executionPlan.noSettingsOrConnectionsChanged')}
                        {node.rerunContext.upstream_reference_uncertain && (
                          <span> · {t('executionPlan.upstreamMayHaveChanged')}</span>
                        )}
                      </div>
                      <div>
                        {t('executionPlan.alreadySpent')}{' '}
                        {node.rerunContext.prior_spent_sparks === null
                          ? t('executionPlan.unavailable')
                          : t('executionPlan.credit', { count: node.rerunContext.prior_spent_sparks })}
                        {showEstimates && node.rerunContext.prior_reserved_sparks != null && node.rerunContext.prior_reserved_sparks > 0 && (
                          <span> · {t('executionPlan.reserved', { count: node.rerunContext.prior_reserved_sparks })}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {showEstimates && (
                  <div className="text-xs font-medium text-text-muted flex-shrink-0">
                    {creditLabel(node.sparks, node.billingMode)}
                  </div>
                )}
              </div>
            )
          })}
          {hiddenNodeCount > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setNodesExpanded((v) => !v)
              }}
              className="w-full px-3 py-1.5 text-xs font-medium text-text-muted hover:text-text-primary hover:bg-surface-2 transition-colors text-left"
            >
              {nodesExpanded ? t('executionPlan.showLess') : t('executionPlan.showMore', { count: hiddenNodeCount })}
            </button>
          )}
        </div>
        {showEstimates && (
          <div className="px-3 py-2 bg-surface-1 border-t border-border-subtle flex justify-between items-center">
            <span className="text-sm font-medium text-text-primary">{t('executionPlan.total')}</span>
            <span className="text-sm font-medium text-text-primary">
              {totalIsFreeTrial ? t('executionPlan.free') : t('executionPlan.credit', { count: totalSparks })}
            </span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {!hasSelection && (
        <div className="flex gap-2 mt-3 animate-fade-in-down" style={{ animationDelay: '200ms' }}>
          <button
            onClick={() => handleSelect('confirm')}
            disabled={disabled || loading}
            className={cn(
              'flex-1 px-3 py-2 text-sm font-medium rounded border transition-colors',
              'bg-chrome text-chrome-foreground border-chrome',
              'hover:bg-chrome/90',
              (disabled || loading) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {loading ? (
              <span className="inline-flex items-center gap-1.5">
                <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                {t('executionPlan.confirming')}
              </span>
            ) : (
              t('executionPlan.letsGo')
            )}
          </button>
          <button
            onClick={() => handleSelect('cancel')}
            disabled={disabled || loading}
            className={cn(
              'px-3 py-2 text-sm font-medium rounded border transition-colors',
              'border-border-subtle text-text-muted',
              'hover:border-border hover:text-text-secondary',
              (disabled || loading) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {t('executionPlan.cancel')}
          </button>
        </div>
      )}
    </div>
  )
})
